import {
  PDC_CAB,
  POLICY_CODING,
  POLICY_TRANSLATION,
  POLICY_CONFIGURATION,
  WORKSPACE_CLOB_KEY,
} from "../constants/Constants";

import api from "api";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { compressToUTF16 } from "lz-string";
import {
  CFUS_RFG,
  CHUS_RFG,
  DUHS_RFG,
} from "containers/PolicyTranslation/Constants/PolicyTranslationConstants";

export function setIndexToRules(rules) {
  return rules.map((r, i) => ({ ...r, index: i }));
}

export function cloneRule(rule) {
  const clonedRule = _.cloneDeep(rule);
  delete clonedRule.RuleKey;
  delete clonedRule.tempId;
  delete clonedRule.tempIdList;
  return clonedRule;
}

export function setTempIdToRules(rules) {
  return rules.map((r) => {
    if (r.tempId) return r;
    return { ...r, tempId: uuidv4() };
  });
}
export default class WorkspaceController {
  constructor(
    policyKey,
    healthplanKey,
    policyCategory,
    policyTaskStatus,
    workspace = null
  ) {
    this.policyKey = policyKey;
    this.healthplanKey = healthplanKey;
    this.policyCategory = policyCategory;
    this.policyTaskStatus = policyTaskStatus;
    this.workspace = workspace;

    if (workspace !== null) this.clob = JSON.parse(workspace.workspaceClob);
  }

  async initialize() {
    if (
      !this.workspace &&
      this.policyKey &&
      this.healthplanKey &&
      this.policyCategory &&
      this.policyTaskStatus
    ) {
      try {
        const response = await api.workspace.getWorkspaceClob(
          this.policyKey,
          this.healthplanKey,
          this.policyCategory,
          this.policyTaskStatus
        );
        this.workspace = response.data;

        if (this.workspace.workspaceClob) {
          this.clob = JSON.parse(this.workspace.workspaceClob);

          // Gather all ProcedureCode values
          const procedureCodes = [];
          this.clob.PolicyBusinessRulesList.forEach((rule) => {
            if (rule.RuleFacts && rule.RuleFacts.ProcedureCode) {
              procedureCodes.push(rule.RuleFacts.ProcedureCode);
            }
          });

          // Check if procedureCodes has data before making the API call
          if (procedureCodes.length > 0) {
            // Fetch descriptions and update the clob
            const descriptions =
              await this.fetchProcedureDescriptions(procedureCodes);

            this.clob.PolicyBusinessRulesList.forEach((rule) => {
              if (rule.RuleFacts && rule.RuleFacts.ProcedureCode) {
                const description = descriptions.find(
                  (desc) => desc.procedureCode === rule.RuleFacts.ProcedureCode
                );
                rule.RuleFacts.ProcedureCodeDescription = description
                  ? description.procedureShortDescription
                  : "--Not Found--";
              }
            });
          } else {
            console.log("No procedure codes found. Skipping API call.");
          }

          this.clob.PolicyBusinessRulesList = setIndexToRules(
            this.clob.PolicyBusinessRulesList
          );
          this.clob.PolicyBusinessRulesList = setTempIdToRules(
            this.clob.PolicyBusinessRulesList
          );

          this.workspace.workspaceClob = JSON.stringify(this.clob);
        }
        // Inside your try block, just before storing:
        const compressedWorkspace = compressToUTF16(
          JSON.stringify(this.workspace)
        );
        localStorage.setItem(WORKSPACE_CLOB_KEY, compressedWorkspace);
      } catch (error) {
        console.error("Error fetching workspace data:", error);
        throw error;
      }
    }
  }

  async fetchProcedureDescriptions(codes) {
    try {
      const response = await api.procedureCode.getProcedureDescriptions(codes);
      return response.data;
    } catch (error) {
      console.error("Error fetching procedure descriptions:", error);
      return {};
    }
  }

  ruleFactory() {
    return {
      RuleActions: {
        PolicyDescriptionAPEAX: "",
        PolicyDescription: "",
        PolicyTag: "",
        DecisionCode: "",
        Error: "",
        EditDescriptionAPEAX: "",
        UpdateObj: "",
      },
      ruleVersionText: "",
      default: {
        Name: "",
      },
      RuleHeader: {
        RuleFlowGroup: "",
        ChangeControl: "",
        PolicyBusinessRuleName: "",
        Priority: "",
        NoLoop: "",
        EditRule: "",
      },
      PolicyHeader: {
        PolicyDescription: "",
        PolicyNumber: "",
        HealthPlanId: "",
      },
      ruleName: "",
      HealthPlanData: {
        HealthPlanGroupIdHigh: "",
        HealthPlanGroupIdLow: "",
        LineOfBusiness: "",
      },
      RuleFacts: {
        RuleEndDate: "",
        ProcedureCode: "",
        ErrorFlag: "",
        DXLimitation: "",
        CoverageCriteriaList: "",
        RuleEffectiveDate: "",
        CodingClassification: "",
        DiagnosisListExpanded: "",
        ProcedureCodeDescription: "",
      },
      ruleKey: "",
      index: 0,
    };
  }
  clobFactory() {
    return {
      lastUpdateDateTime: "",
      ruleFlowGroupVersionText: "",
      PolicyBusinessRulesListCount: 0,
      ruleFlowGroupKey: "",
      PolicyBusinessRulesList: [],
    };
  }
  createNewClob() {
    return this.clobFactory();
  }

  getClob() {
    return this.clob;
  }
  setClob(newClob) {
    if (newClob?.PolicyBusinessRulesList) {
      newClob.PolicyBusinessRulesList = setIndexToRules(
        newClob.PolicyBusinessRulesList
      );
      newClob.PolicyBusinessRulesList = setTempIdToRules(
        newClob.PolicyBusinessRulesList
      );
    }

    return (this.clob = newClob);
  }

  cloneController() {
    return _.cloneDeep(this);
  }

  getClobPolicyRulesList() {
    if (!this.clob) return [];
    return this.clob.PolicyBusinessRulesList;
  }

  getClobPolicyBusinessRulesCount() {
    return this.clob?.PolicyBusinessRulesListCount;
  }

  getPolicyHeader(index) {
    return this.clob?.PolicyBusinessRulesList?.[index]?.PolicyHeader;
  }

  getProcedureCode(index) {
    return this.clob?.PolicyBusinessRulesList?.[index]?.RuleFacts
      ?.ProcedureCode;
  }

  getRuleHeader(index) {
    return this.clob?.PolicyBusinessRulesList?.[index]?.RuleHeader;
  }

  getRuleFacts(index) {
    return this.clob?.PolicyBusinessRulesList?.[index]?.RuleFacts;
  }
  removeRule(index) {
    this.clob.PolicyBusinessRulesList = [
      ...this.clob.PolicyBusinessRulesList.slice(0, index),
      ...this.clob.PolicyBusinessRulesList.slice(index + 1),
    ];
    this.clob.PolicyBusinessRulesList = setIndexToRules(
      this.clob.PolicyBusinessRulesList
    );
    this.clob.PolicyBusinessRulesList = setTempIdToRules(
      this.clob.PolicyBusinessRulesList
    );
    return this;
  }

  /**
   * Adds one or more business rules to the PolicyBusinessRulesList.
   * If the provided rule is an array, all rules in the array are added.
   * Otherwise, the single rule is added to the list.
   * After adding, the method updates the indices and temporary IDs for all rules.
   *
   * @param {Object|Object[]} rule - A single business rule object or an array of business rule objects to add.
   * @returns {WorkspaceController} The updated WorkspaceController instance.
   */
  addBusinessRule(rule) {
    if (!this.clob) {
      this.clob = this.createNewClob();
    }
    if (Array.isArray(rule)) {
      this.clob.PolicyBusinessRulesList = [
        ...this.clob.PolicyBusinessRulesList,
        ...rule,
      ];
    } else {
      this.clob.PolicyBusinessRulesList = [
        ...this.clob.PolicyBusinessRulesList,
        rule,
      ];
    }
    this.clob.PolicyBusinessRulesList = setIndexToRules(
      this.clob.PolicyBusinessRulesList
    );
    this.clob.PolicyBusinessRulesList = setTempIdToRules(
      this.clob.PolicyBusinessRulesList
    );
    return this;
  }
  setRuleVersion(index, value) {
    this.clob.PolicyBusinessRulesList[index].RuleHeader.RuleVersion = value;
    return this;
  }
  incrementRuleVersion(index) {
    const healthPlanKey = this.workspace.healthPlanKey;
    const editRule =
      this.clob.PolicyBusinessRulesList[index].RuleHeader.EditRule;
    const HealthPlanId = this.clob.HealthPlanId;
    const step = 0.1;
    if (healthPlanKey != "999" && editRule === "VARIANCE") {
      let oldVersion =
        this.clob.PolicyBusinessRulesList[index].RuleHeader.RuleVersion;
      oldVersion = this.determineVarianceVersionNumber(
        oldVersion,
        HealthPlanId,
        step
      );
      this.clob.PolicyBusinessRulesList[index].RuleHeader.RuleVersion =
        oldVersion;
    } else {
      const step = 0.1;
      const oldVersion =
        this.clob.PolicyBusinessRulesList[index].RuleHeader.RuleVersion;
      this.clob.PolicyBusinessRulesList[index].RuleHeader.RuleVersion =
        oldVersion
          ? Math.round((oldVersion + step) * 10) / 10
          : Math.round((1 + step) * 10) / 10;
    }
    return this;
  }
  setCoverageCriteriaList(index, value) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.CoverageCriteriaList =
      value;
    return this;
  }
  setClassification(index, classification) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.CodingClassification =
      classification;
    return this;
  }
  setRuleEffectiveDate(index, ruleEffectiveDate) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.RuleEffectiveDate =
      ruleEffectiveDate;
    return this;
  }
  setLobConfigKey(index, value) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.LobConfigKey = value;
    return this;
  }
  duplicateRule(index) {
    const originalRule = this.clob.PolicyBusinessRulesList[index];
    const clonedRule = cloneRule(originalRule);

    // Copy the entire group tempIdList to the new rule
    clonedRule.tempIdList = [
      ...(originalRule.tempIdList || [originalRule.tempId]),
    ];

    this.clob.PolicyBusinessRulesList = [
      ...this.clob.PolicyBusinessRulesList.slice(0, index + 1),
      clonedRule,
      ...this.clob.PolicyBusinessRulesList.slice(index + 1),
    ];
    this.clob.PolicyBusinessRulesList = setIndexToRules(
      this.clob.PolicyBusinessRulesList
    );
    this.clob.PolicyBusinessRulesList = setTempIdToRules(
      this.clob.PolicyBusinessRulesList
    );
    return this;
  }
  //workspace controller
  setEditRule(index, editRule) {
    const HealthPlanId = this.clob.HealthPlanId;
    if (editRule === "NEW_OR_VARIANCE" && HealthPlanId === "AHCS") {
      this.clob.PolicyBusinessRulesList[index].RuleHeader.EditRule = "NEW";
    } else if (editRule === "NEW_OR_VARIANCE" && HealthPlanId !== "AHCS") {
      this.clob.PolicyBusinessRulesList[index].RuleHeader.EditRule = "VARIANCE";
    } else {
      this.clob.PolicyBusinessRulesList[index].RuleHeader.EditRule = editRule;
    }
    return this;
  }

  getEditRule(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleHeader.EditRule;
  }

  getDiagnosisListExpanded(index) {
    return (
      this.clob.PolicyBusinessRulesList[index].RuleFacts
        .DiagnosisListExpanded || []
    );
  }

  setDiagnosisListExpanded(index, dxCodes) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.DiagnosisListExpanded =
      dxCodes;
    return this;
  }
  setDiagnosisListCondensed(index, dxCodes) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.DiagnosisListCondensed =
      dxCodes;
    return this;
  }

  getHealthPlanData(index) {
    return this.clob.PolicyBusinessRulesList[index].HealthPlanData;
  }

  getRuleActions(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleActions;
  }

  getRuleExclusionData(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleExclusionData;
  }

  getUseCase(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleHeader.UseCase || "";
  }

  //This probably doesn't need to live here since it's urealated to workspace calls
  getDiagnosisDescriptions(diagnosisCodes) {
    return api.diagnosisCode.getDiagnosisCodeDescriptions(diagnosisCodes);
  }

  setRuleEndDate(index, ruleEndDate) {
    this.clob.PolicyBusinessRulesList[index].RuleFacts.RuleEndDate =
      ruleEndDate;
    return this;
  }

  setPolicyBusinessRuleName(index, policyBusinessRuleName) {
    this.clob.PolicyBusinessRulesList[index].RuleHeader.PolicyBusinessRuleName =
      policyBusinessRuleName;
    return this;
  }

  getPolicyBusinessRuleName(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleHeader
      .PolicyBusinessRuleName;
  }

  getRuleVersion(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleHeader.RuleVersion;
  }

  getChangeControl(index) {
    return this.clob.PolicyBusinessRulesList[index].RuleHeader.ChangeControl;
  }

  setUseCase(index, useCase) {
    this.clob.PolicyBusinessRulesList[index].RuleHeader.UseCase = useCase;
    return this;
  }

  setRule(index, rule) {
    this.clob.PolicyBusinessRulesList[index] = rule;
    return this;
  }

  getPolicyState(
    healthPlanDisplayName,
    category,
    policyStatus,
    codingStatus,
    translationStatus,
    userRole,
    skills
  ) {
    const effectiveSkills = skills || [];
    let policyState = {};
    switch (category) {
      case PDC_CAB:
        policyState.screenRoute = "";
        break;
      case POLICY_CODING:
        policyState.editMode = this.#determinePolicyCodingFormMode(
          userRole,
          effectiveSkills,
          codingStatus
        );
        policyState.screenRoute = "/policy-coding";
        break;
      case POLICY_TRANSLATION:
        policyState.editMode = this.#determinePolicyTranslationFormMode(
          userRole,
          effectiveSkills,
          translationStatus
        );
        policyState.screenRoute = "/policy-rules";
        break;
      case POLICY_CONFIGURATION:
        policyState.screenRoute = "/use-cases";
        policyState.openUseCases = this.#openUseCases(
          codingStatus,
          translationStatus
        );
        policyState.editMode = this.#determinePolicyConfigurationFormMode(
          userRole,
          effectiveSkills,
          codingStatus,
          translationStatus,
          policyStatus.toLowerCase()
        );
    }
    policyState.healthPlanDisplayName = healthPlanDisplayName;
    /*TODO: Add logic for Translation to determine if the user can edit the form to the
      switch above before removing this section.
    */
    if (
      category !== POLICY_CODING &&
      category !== POLICY_TRANSLATION &&
      category !== POLICY_CONFIGURATION
    ) {
      if (policyStatus && policyStatus.toLowerCase() === "complete") {
        policyState.editMode = false;
      } else {
        policyState.editMode = true;
      }
    }

    return policyState;
  }

  determineVarianceVersionNumber(oldVersion, HealthPlanId, step) {
    let varianceVersionNumber = 1.0;
    let rootVarianceNumber = 1.0;
    if (oldVersion && oldVersion.toString().includes("-")) {
      const parts = oldVersion.toString().split("-");
      rootVarianceNumber = parseFloat(parts[0]);
      varianceVersionNumber = parseFloat(parts[parts.length - 1]);
      varianceVersionNumber = varianceVersionNumber + step;
    } else {
      varianceVersionNumber = 1.0;
      rootVarianceNumber = oldVersion ? oldVersion : 1.0;
    }
    const newVersion =
      parseFloat(rootVarianceNumber).toFixed(1) +
      "-" +
      HealthPlanId +
      "-" +
      varianceVersionNumber.toFixed(1);

    return newVersion;
  }

  updateChangeControlForAllRules(changeControl) {
    this.clob.PolicyBusinessRulesList.forEach((rule) => {
      if (["NEW", "VARIANCE"].includes(rule.RuleHeader.EditRule)) {
        rule.RuleHeader.ChangeControl = changeControl;
      }
    });
    return this;
  }

  //This method is used to split rules by their adoption decision
  splitRulesByAdoptionDecision() {
    let duplicateRules = [];

    for (let i = 0; i < this.clob.PolicyBusinessRulesList.length; i++) {
      const rule = this.clob.PolicyBusinessRulesList[i];
      const LobConfigKey = rule.RuleFacts?.LobConfigKey || null;

      if (!LobConfigKey) continue; // Skip if LobConfigKey is null or undefined

      if (Array.isArray(LobConfigKey) && LobConfigKey.length > 0) {
        // If LobConfigKey is an array, create a new rule for each value
        for (let j = 0; j < LobConfigKey.length; j++) {
          const newRule = cloneRule(rule);
          const deleteRule = LobConfigKey[j].endDate === "" ? false : true;
          newRule.RuleFacts.LobConfigKey = LobConfigKey[j].LobConfigKey
            ? String(LobConfigKey[j].LobConfigKey)
            : "";
          newRule.RuleFacts.RuleEffectiveDate = LobConfigKey[j].effectiveDate;
          newRule.RuleFacts.RuleEndDate = LobConfigKey[j].endDate;
          if (deleteRule) {
            newRule.RuleHeader.EditRule = "DELETED";
          }
          duplicateRules.push(newRule);
        }

        // Remove the original rule from the list
        this.removeRule(i);
        i--; // Adjust index after removal
      }
    }

    // Add the cloned rules to the original rules list
    this.addBusinessRule(duplicateRules);
  }

  //Combine LobConfigKey values in to a single rule where the PolicyBusinessRuleName + RuleVersion are the same
  combineRulesByAdoptionDecision() {
    const ruleMap = {};
    let ruleCounter = 0;
    const asIsRules = [];

    for (const rule of this.clob.PolicyBusinessRulesList) {
      const ruleName = rule.RuleHeader?.PolicyBusinessRuleName || ruleCounter++;
      const ruleVersion = rule.RuleHeader?.RuleVersion;
      const endDate = rule.RuleFacts?.RuleEndDate || "";
      const editRule = rule.RuleHeader?.EditRule || "";
      let lobConfigKey = rule.RuleFacts?.LobConfigKey ?? null;

      const mapKey =
        ruleVersion == null
          ? `${ruleName}||<nover>`
          : `${ruleName}||${ruleVersion}`;

      // For "as-is" rules, just collect for the final output
      if (editRule === "DELETED" || !lobConfigKey || lobConfigKey === "") {
        asIsRules.push(rule);
        continue;
      }

      // Combine logic as before
      let keyObjects = [];
      if (Array.isArray(lobConfigKey)) {
        keyObjects = lobConfigKey
          .filter((obj) => obj && obj.LobConfigKey)
          .map((obj) => ({
            LobConfigKey: obj.LobConfigKey,
            effectiveDate:
              obj.effectiveDate ?? rule.RuleFacts?.RuleEffectiveDate,
            endDate: obj.endDate ?? endDate,
          }));
      } else {
        keyObjects = [
          {
            LobConfigKey: lobConfigKey,
            effectiveDate: rule.RuleFacts?.RuleEffectiveDate,
            endDate: endDate,
          },
        ];
      }

      if (!ruleMap[mapKey]) {
        const newRule = cloneRule(rule);
        newRule.RuleFacts.LobConfigKey = [];
        ruleMap[mapKey] = newRule;
      }

      const arr = ruleMap[mapKey].RuleFacts.LobConfigKey;
      for (const obj of keyObjects) {
        if (!arr.some((e) => e.LobConfigKey === obj.LobConfigKey)) {
          arr.push(obj);
        }
      }
    }

    // Use addBusinessRule to set indexes and tempIds
    this.clob.PolicyBusinessRulesList = [];
    this.addBusinessRule([...asIsRules, ...Object.values(ruleMap)]);
  }

  //Private helper methods

  //Determines if the user has the ability to edit the policy coding form
  #determinePolicyCodingFormMode(userRole, skills, policyStatus) {
    const lowerCaseStatus = policyStatus ? policyStatus.toLowerCase() : "";

    if (userRole === "POET-Avalon-Admin") {
      if (
        ["assigned", "in progress", "peer review", "pdc review"].includes(
          lowerCaseStatus
        )
      ) {
        return true; // Edit mode
      } else {
        return false; // Read Only mode
      }
    } else if (userRole === "POET-Avalon-POE") {
      if (skills.includes("Coding") || skills.includes("Manager")) {
        if (["assigned", "in progress"].includes(lowerCaseStatus)) {
          return true; // Edit mode
        }
      }

      if (skills.includes("Coding Review") || skills.includes("Manager")) {
        if (["peer review", "pdc review"].includes(lowerCaseStatus)) {
          return true; // Edit mode
        }
      }

      // All other skill/status combinations
      return false; // Read Only mode
    } else {
      // All other user roles
      return false; // Read Only mode
    }
  }

  //Determines if the user has the ability to edit the policy translation form
  #determinePolicyTranslationFormMode(userRole, skills, policyStatus) {
    const lowerCaseStatus = policyStatus ? policyStatus.toLowerCase() : "";

    if (userRole === "POET-Avalon-Admin") {
      if (
        ["assigned", "in progress", "peer review", "pdc review"].includes(
          lowerCaseStatus
        )
      ) {
        return true; // Edit mode
      } else {
        return false; // Read Only mode
      }
    } else if (userRole === "POET-Avalon-POE") {
      const hasEditSkill =
        skills.includes("Admin") ||
        skills.includes("Manager") ||
        skills.includes("Translation") ||
        skills.includes("Translation Review");
      if (hasEditSkill) {
        if (
          ["assigned", "in progress", "peer review", "pdc review"].includes(
            lowerCaseStatus
          )
        ) {
          return true; // Edit mode
        }
      }

      // All other skill/status combinations
      return false; // Read Only mode
    } else {
      // All other user roles
      return false; // Read Only mode
    }
  }

  //Determines if the user has the ability to edit the policy translation form
  #determinePolicyConfigurationFormMode(
    userRole,
    skills,
    codingStatus,
    translationStatus,
    configurationStatus
  ) {
    if (configurationStatus === "complete") {
      return false; // Read Only mode
    }
    if (
      codingStatus.toLowerCase() === "complete" &&
      translationStatus.toLowerCase() === "complete" &&
      userRole === "POET-Avalon-Admin"
    ) {
      return true; // Edit mode
    } else if (
      codingStatus.toLowerCase() === "complete" &&
      translationStatus.toLowerCase() === "complete" &&
      userRole === "POET-Avalon-POE"
    ) {
      if (
        skills.includes("Configuration") ||
        skills.includes("Configuration Review") ||
        skills.includes("manager") ||
        skills.includes("Manager")
      ) {
        return true; // Edit mode
      }

      // All other skill/status combinations
      return false; // Read Only mode
    } else {
      // All other user roles
      return false; // Read Only mode
    }
  }

  #openUseCases(codingStatus, translationStatus) {
    return (
      codingStatus.toLowerCase() === "complete" &&
      translationStatus.toLowerCase() === "complete"
    );
  }
}
